import { isMissing, partialNote } from "@/profitability/missing";
import { listWords } from "@/profitability/records/record-view/missing-fields";
import { blankLines, citationPlace, isFilePart } from "./sourced";
import type { FigureHow, FigurePart, RunFigure } from "./types";

const CHOICE: Partial<Record<FigureHow, [two: string, more: string]>> = {
  largest: ["Larger", "Largest"],
  smallest: ["Smaller", "Smallest"],
  first: ["First", "First"],
};

/** Lowercase a title-case name mid-sentence. Leave an acronym. */
const midSentence = (name: string) =>
  /^[A-Z][a-z]/.test(name) ? name[0].toLowerCase() + name.slice(1) : name;

/** The script writes the figure's name as a title; mid-sentence it is a word. */
export const shareSentence = (note: string) =>
  note.replace(
    /^(\S+ of \S+ )([^,]+)/,
    (_, lead: string, name: string) => lead + midSentence(name),
  );

const plural = (count: number, word: string) =>
  `${count} ${word}${count === 1 ? "" : "s"}`;

export const fileTitle = (
  citation: Pick<FigurePart, "input_title" | "file_name" | "input">,
) => citation.input_title ?? citation.file_name ?? citation.input ?? "A file";

export const splitNote = (how: FigureHow, note: string | null) => {
  if (!note) return { candidate: null, remark: null };
  if (!CHOICE[how]) return { candidate: null, remark: note };
  const at = note.indexOf(": ");
  return at < 0
    ? { candidate: note, remark: null }
    : { candidate: note.slice(0, at), remark: note.slice(at + 2) };
};

export const figureCandidates = (figure: RunFigure) => {
  const names: string[] = [];
  let chosen: string | null = null;
  for (const part of figure.parts) {
    const name =
      splitNote(figure.how, part.note).candidate ??
      (isFilePart(part) ? fileTitle(part) : null);
    if (!name) continue;
    if (!names.includes(name)) names.push(name);
    if (part.used && chosen === null)
      chosen = isFilePart(part) ? fileTitle(part) : name;
  }
  return { names, chosen };
};

/**
 * Null when there is nothing to say (missing, where `missingWhy` speaks).
 * `hasFile`: the column's file is known, though no row of it is.
 */
export const figureHow = (
  figure: RunFigure,
  hasFile = false,
): string | null => {
  const files = [
    ...new Set(figure.parts.filter(isFilePart).map((part) => fileTitle(part))),
  ];
  switch (figure.how) {
    case "cell": {
      const cell = figure.parts.find(isFilePart);
      return cell ? citationPlace(cell) : null;
    }
    case "sum": {
      const leftOut = figure.parts.filter((part) => !part.used).length;
      const where =
        files.length === 1
          ? ` in ${files[0]}`
          : files.length > 1
            ? ` in ${files.length} files`
            : "";
      // Only the first parts are listed: how many of the rest were left
      // out is not known, so the count is all of them and the left out
      // are those of the listed.
      if (figure.count > figure.parts.length)
        return leftOut
          ? `${plural(figure.count, "line")}${where}, ${leftOut} of the ${figure.parts.length} shown left out`
          : `Sum of ${plural(figure.count, "line")}${where}`;
      return `Sum of ${plural(figure.count - leftOut, "line")}${where}${leftOut ? `, ${leftOut} left out` : ""}`;
    }
    case "largest":
    case "smallest":
    case "first": {
      const { names, chosen } = figureCandidates(figure);
      const [two, more] = CHOICE[figure.how] ?? ["", ""];
      const head = `${names.length === 2 ? two : more} of ${names.length}`;
      return chosen ? `${head}, from ${chosen}` : head;
    }
    case "share":
      return figure.note
        ? shareSentence(figure.note)
        : "A share of another figure";
    case "total":
      return figure.note ?? `Sum of ${plural(figure.count, "row")}`;
    case "formula":
      return figure.note ?? "Worked out from other figures";
    case "setting": {
      const name = figure.parts[0]?.note ?? figure.note;
      return name ? `From the ${name} setting` : "From a setting";
    }
    case "none":
      if (isMissing(figure.value)) return null;
      return hasFile
        ? "Not traced to a row of the file"
        : "Not traced to a file";
  }
};

/**
 * A formula missing another figure is not a gap of its own: it waits on the
 * roots those operands trace to. Null when the figure is not missing.
 */
export const missingWhy = (
  figure: RunFigure,
  roots?: string[] | null,
): string | null => {
  if (!isMissing(figure.value)) return null;
  switch (figure.how) {
    case "cell":
      return "Blank in the file";
    case "sum": {
      const lines = blankLines(figure);
      if (lines && lines.blank === lines.total)
        return lines.total === 1
          ? "Blank in the file"
          : `All ${lines.total} lines blank`;
      return figure.note ?? "No lines to add up";
    }
    case "largest":
    case "smallest":
    case "first": {
      const { names } = figureCandidates(figure);
      if (names.length === 2) return "Not in either source";
      return names.length > 2
        ? `Not in any of its ${names.length} sources`
        : "Blank in the file";
    }
    case "share":
      return figure.note ?? "Nothing to share out";
    case "total":
      return figure.count
        ? `Missing for all ${plural(figure.count, "row")}`
        : "No rows to add up";
    case "formula": {
      const missing = figure.parts.filter(
        (part) => part.used && isMissing(part.value),
      );
      const operands = missing.flatMap((part) =>
        part.figure ? [part.note ?? part.figure] : [],
      );
      if (operands.length)
        return `Can't be worked out without ${listWords(roots?.length ? roots : operands)}`;
      const settings = missing.flatMap((part) =>
        part.setting ? [part.note ?? part.setting] : [],
      );
      if (settings.length)
        return settings.length === 1
          ? `The ${settings[0]} setting isn't set`
          : `The ${listWords(settings)} settings aren't set`;
      const others = missing.flatMap((part) => (part.note ? [part.note] : []));
      return others.length
        ? `${listWords(others)} ${others.length === 1 ? "is" : "are"} missing`
        : (figure.note ?? "Worked out from a missing figure");
    }
    case "setting":
      return "The setting isn't set";
    case "none":
      return figure.note ?? "Not in this period's files";
  }
};

/** Nothing matched: a missing file or line, unlike a blank cell. */
export const isNoMatch = (figure: RunFigure | null | undefined) =>
  !!figure && figure.how === "none" && isMissing(figure.value);

/** Null when the figure itself is missing. */
export const partialWords = (figure: RunFigure | null | undefined) => {
  if (!figure || isMissing(figure.value)) return null;
  if (figure.missing?.count)
    return partialNote(figure.missing.count, figure.missing.of);
  const lines = blankLines(figure);
  return lines
    ? `${lines.blank} of ${plural(lines.total, "line")} blank`
    : null;
};

export const partPlace = (how: FigureHow, part: FigurePart) => {
  if (isFilePart(part)) return citationPlace(part) ?? fileTitle(part);
  const { candidate, remark } = splitNote(how, part.note);
  return candidate ?? remark ?? "A value";
};

/** A figure or a setting has no remark of its own; a bare candidate does. */
export const partRemark = (how: FigureHow, part: FigurePart) => {
  const { candidate, remark } = splitNote(how, part.note);
  if (isFilePart(part)) return remark;
  if (part.figure || part.setting) return null;
  return candidate ? remark : null;
};

export const shareWords = (share: number | null) =>
  share === null
    ? null
    : `${(share * 100).toLocaleString(undefined, { maximumFractionDigits: 1 })}% counted`;
